import React, { useEffect, useRef } from 'react';
import { motion, useTransform, useMotionValueEvent } from 'framer-motion';

/*
 Scroll-driven flat-vector scene (viewBox 1600 x 700, content extends to x ≈ 2300; the camera pans).
 Timeline (progress 0..1):
   0.00-0.20 pallets packed into the container, doors close
   0.20-0.26 reach stacker drives in and locks onto the box
   0.26-0.35 stacker lifts, carries and lowers the box onto the trailer (behind the cab)
   0.37-0.60 truck drives to the terminal
   0.60-0.86 ship-to-shore crane lifts the container onto the vessel
   0.86-1.00 vessel sails
*/
const INK = '#0F1B26', BLUE = '#1F97CC', BLUE600 = '#0A6EA8', G100 = '#EEF1F3', G200 = '#E1E5E8', G300 = '#D1D7DC', G400 = '#AEB7BE', G500 = '#86929B', G700 = '#46525C', BLUE100 = '#E3F2FA', BLUE300 = '#A6D6EE';

// Container: 120 x 48 (a 40' box). Trailer top y=440 → container y=392. Ship top tier lands at y=326.
const C_W = 120, C_H = 48;
const TRUCK_TRAVEL = 700;
const SHIP_BAY_X = 1440; // first bay where our box lands
const SHIP_TRAVEL = 700;

export function JourneyScene({ progress }) {
  // Camera: focus point + zoom → single SVG transform (attribute driven directly, Framer would override it)
  const camS = useTransform(progress, [0, 0.3, 0.45, 0.62, 0.86, 1], [1.1, 1.1, 0.8, 0.72, 0.72, 0.7]);
  const camFx = useTransform(progress, [0, 0.3, 0.45, 0.62, 0.86, 1], [380, 380, 850, 1480, 1480, 1750]);
  const camFy = useTransform(progress, [0, 0.3, 0.45, 0.62, 0.86, 1], [345, 345, 340, 345, 345, 345]);
  const camTransform = useTransform([camS, camFx, camFy], function ([s, fx, fy]) {
    return 'translate(' + (800 - fx * s) + ' ' + (380 - fy * s) + ') scale(' + s + ')';
  });
  const camRef = useRef(null);
  useMotionValueEvent(camTransform, 'change', function (v) { if (camRef.current) camRef.current.setAttribute('transform', v); });
  useEffect(function () { if (camRef.current) camRef.current.setAttribute('transform', camTransform.get()); }, [camTransform]);

  // Pallets into the container (container sits at x=200..320 on the ground)
  const b1x = useTransform(progress, [0.0, 0.08], [50, 212]);
  const b2x = useTransform(progress, [0.04, 0.12], [90, 246]);
  const b3x = useTransform(progress, [0.08, 0.16], [130, 280]);
  const doorOpacity = useTransform(progress, [0.16, 0.2], [0, 1]);
  const boxesOpacity = useTransform(progress, [0.19, 0.2], [1, 0]);

  // Container path (x is locked to the trailer from touchdown until the truck departs at 0.37)
  const cx = useTransform(progress,
    [0, 0.29, 0.32, 0.37, 0.6, 0.7, 0.76, 0.86, 1],
    [200, 200, 395, 395, 395 + TRUCK_TRAVEL, 395 + TRUCK_TRAVEL, SHIP_BAY_X, SHIP_BAY_X, SHIP_BAY_X + SHIP_TRAVEL]);
  const cy = useTransform(progress,
    [0, 0.26, 0.29, 0.32, 0.35, 0.65, 0.7, 0.76, 0.82, 1],
    [382, 382, 300, 300, 392, 392, 180, 180, 326, 326]);

  // Truck (tractor at the front/right, trailer behind it)
  const truckX = useTransform(progress, [0, 0.37, 0.6, 0.82, 0.95], [0, 0, TRUCK_TRAVEL, TRUCK_TRAVEL, -900]);
  const wheelRot = useTransform(progress, [0.37, 0.6], [0, 1440]);

  // Reach stacker: drives in, spreader locks on the box top, lifts, carries, lowers, releases
  const stackerX = useTransform(progress, [0, 0.2, 0.24, 0.29, 0.32], [-130, -130, 0, 0, 195]);
  const stackerArmY = useTransform(progress, [0, 0.24, 0.26, 0.29, 0.32, 0.35, 0.36, 0.39], [-50, -50, 0, -82, -82, 10, 10, -50]);
  const boomY2 = useTransform(stackerArmY, function (v) { return 377 + v; });

  // Crane trolley + spreader (spreader group origin = container x)
  const spreaderX = useTransform(progress, [0, 0.7, 0.76, 1], [395 + TRUCK_TRAVEL, 395 + TRUCK_TRAVEL, SHIP_BAY_X, SHIP_BAY_X]);
  const spreaderY = useTransform(progress, [0, 0.6, 0.65, 0.7, 0.76, 0.82, 0.86, 1], [140, 140, 392, 180, 180, 326, 140, 140]);
  const cableH = useTransform(spreaderY, function (v) { return Math.max(v - 124, 0); });

  // Vessel
  const shipX = useTransform(progress, [0, 0.86, 1], [0, 0, SHIP_TRAVEL]);
  const wakeOpacity = useTransform(progress, [0.86, 0.9], [0, 1]);

  const deckBays = [1570, 1700, 1830, 1960]; // x of loaded bays (SHIP_BAY_X keeps its top tier free)
  const tiers = [422, 374, 326];

  return (
    <svg viewBox="0 0 1600 700" className="w-full h-full" preserveAspectRatio="xMidYMid slice" data-testid="journey-scene" aria-hidden>
      <rect width="1600" height="700" fill="#F6F8F9" />
      <g ref={camRef}>
        {/* Ground, road and quay */}
        <rect x="-600" y="430" width="1900" height="400" fill={G100} />
        <rect x="-600" y="470" width="1900" height="60" fill={G300} />
        <line x1="-600" y1="500" x2="1300" y2="500" stroke="#fff" strokeOpacity="0.9" strokeWidth="3" strokeDasharray="36 28" />
        <rect x="1290" y="430" width="14" height="80" fill={INK} />
        {/* Sea */}
        <rect x="1300" y="470" width="1600" height="400" fill={BLUE100} />
        <g stroke={BLUE300} strokeWidth="2">
          <line x1="1330" y1="580" x2="1420" y2="580" /><line x1="1500" y1="620" x2="1620" y2="620" /><line x1="1720" y1="590" x2="1810" y2="590" /><line x1="2150" y1="560" x2="2260" y2="560" /><line x1="2400" y1="600" x2="2500" y2="600" />
        </g>

        {/* Warehouse */}
        <g>
          <rect x="30" y="250" width="340" height="180" fill={G300} stroke={INK} strokeWidth="2" />
          <polygon points="30,250 200,190 370,250" fill={G400} stroke={INK} strokeWidth="2" />
          <rect x="60" y="264" width="220" height="26" fill={INK} />
          <text x="72" y="282" fill="#fff" fontFamily="Figtree" fontWeight="500" fontSize="14">Südvik depot · Copenhagen</text>
          <rect x="60" y="310" width="60" height="40" fill={BLUE100} stroke={INK} strokeWidth="2" /><rect x="140" y="310" width="60" height="40" fill={BLUE100} stroke={INK} strokeWidth="2" /><rect x="220" y="310" width="60" height="40" fill={BLUE100} stroke={INK} strokeWidth="2" />
          <rect x="30" y="395" width="170" height="35" fill={G700} />
          <rect x="-600" y="426" width="1000" height="6" fill={G500} />
        </g>

        {/* Pallets */}
        <motion.g style={{ opacity: boxesOpacity }}>
          {[b1x, b2x, b3x].map(function (bx, i) {
            return (
              <motion.g key={i} style={{ x: bx }}>
                <rect x="0" y="400" width="30" height="30" fill={G200} stroke={INK} strokeWidth="2" />
                <line x1="0" y1="415" x2="30" y2="415" stroke={INK} strokeWidth="2" />
                <line x1="15" y1="400" x2="15" y2="430" stroke={INK} strokeWidth="2" />
              </motion.g>
            );
          })}
        </motion.g>

        {/* Reach stacker (spreader bar sits flush on the container top: base y 372..382 = box top on the ground) */}
        <motion.g style={{ x: stackerX }}>
          <rect x="110" y="380" width="70" height="50" fill={G700} />
          <rect x="120" y="360" width="40" height="24" fill={INK} />
          <rect x="144" y="364" width="12" height="12" fill={BLUE100} />
          <circle cx="125" cy="432" r="12" fill={INK} stroke={G400} strokeWidth="3" />
          <circle cx="170" cy="432" r="12" fill={INK} stroke={G400} strokeWidth="3" />
          <motion.line x1="176" y1="372" x2="260" style={{ y2: boomY2 }} stroke={G700} strokeWidth="12" strokeLinecap="round" />
          <circle cx="176" cy="372" r="7" fill={INK} />
          <motion.g style={{ y: stackerArmY }}>
            <rect x="250" y="360" width="20" height="14" fill={G700} />
            <rect x="200" y="372" width={C_W} height="10" fill={INK} />
            <rect x="202" y="382" width="8" height="5" fill={G500} /><rect x="310" y="382" width="8" height="5" fill={G500} />
          </motion.g>
        </motion.g>

        {/* Truck: trailer (left) + tractor (right, front) */}
        <motion.g style={{ x: truckX }}>
          <rect x="380" y="440" width="150" height="10" fill={G700} />
          <rect x="384" y="450" width="10" height="12" fill={G700} />
          <rect x="526" y="446" width="24" height="8" fill={G700} />
          <rect x="540" y="452" width="80" height="10" fill={G700} />
          <rect x="544" y="396" width="66" height="56" fill={INK} />
          <rect x="588" y="404" width="18" height="22" fill={BLUE100} />
          <rect x="548" y="404" width="34" height="22" fill={G700} />
          <rect x="606" y="430" width="14" height="24" fill={G700} />
          <rect x="544" y="380" width="8" height="16" fill={G500} />
          {[400, 436, 560, 600].map(function (wx) {
            return (
              <motion.g key={wx} style={{ rotate: wheelRot }}>
                <circle cx={wx} cy="470" r="15" fill={INK} stroke={G400} strokeWidth="4" />
                <line x1={wx - 8} y1="470" x2={wx + 8} y2="470" stroke={G400} strokeWidth="3" />
              </motion.g>
            );
          })}
        </motion.g>

        {/* Terminal yard stacks */}
        <g>
          {[[960, 382, G300], [960, 334, G400], [1090, 382, G200], [1090, 334, G300], [960, 286, G200]].map(function (s, i) {
            return <rect key={i} x={s[0]} y={s[1]} width={C_W} height={C_H} fill={s[2]} stroke={INK} strokeWidth="2" />;
          })}
          <text x="960" y="262" fill={G700} fontFamily="IBM Plex Mono" fontSize="13">Terminal 04 · berth 2</text>
        </g>

        {/* Ship-to-shore crane */}
        <g>
          <rect x="1010" y="110" width="720" height="14" fill={G500} />
          <rect x="1150" y="124" width="16" height="306" fill={G500} /><rect x="1270" y="124" width="16" height="306" fill={G500} />
          <rect x="1140" y="100" width="156" height="10" fill={G400} />
          <polygon points="1158,124 1278,124 1218,240" fill="none" stroke={G500} strokeWidth="6" />
          <rect x="1130" y="420" width="170" height="14" fill={G700} />
          <rect x="1300" y="424" width="18" height="10" fill={G700} />
        </g>

        {/* Spreader + trolley */}
        <motion.g style={{ x: spreaderX }}>
          <rect x="40" y="96" width="40" height="18" fill={INK} />
          <rect x="30" y="112" width="60" height="12" fill={G700} />
          <motion.rect x="56" y="124" width="8" fill={G500} style={{ height: cableH }} />
          <motion.g style={{ y: spreaderY }}>
            <rect x="0" y="-12" width={C_W} height="12" fill={INK} />
            <rect x="-4" y="-4" width="8" height="8" fill={G500} /><rect x={C_W - 4} y="-4" width="8" height="8" fill={G500} />
          </motion.g>
        </motion.g>

        {/* Vessel */}
        <motion.g style={{ x: shipX }}>
          <motion.g style={{ opacity: wakeOpacity }} stroke={BLUE} strokeOpacity="0.6" strokeWidth="3" fill="none">
            <path d="M1322 520 Q 1280 532 1230 524" /><path d="M1326 548 Q 1270 566 1200 552" />
          </motion.g>
          <polygon points="1320,470 2080,470 2130,505 2060,548 1350,548" fill={INK} />
          <rect x="1320" y="474" width="760" height="5" fill={BLUE} />
          <rect x="1320" y="452" width="760" height="18" fill={G700} />
          {/* accommodation block at the stern */}
          <rect x="1340" y="300" width="96" height="152" fill="#fff" stroke={INK} strokeWidth="2" />
          <rect x="1328" y="300" width="120" height="22" fill="#fff" stroke={INK} strokeWidth="2" />
          <rect x="1350" y="306" width="76" height="10" fill={BLUE100} />
          {[334, 358, 382, 406].map(function (wy) { return <g key={wy}><rect x="1352" y={wy} width="14" height="10" fill={BLUE100} /><rect x="1380" y={wy} width="14" height="10" fill={BLUE100} /><rect x="1408" y={wy} width="14" height="10" fill={BLUE100} /></g>; })}
          <rect x="1370" y="270" width="26" height="30" fill={INK} />
          <rect x="1440" y="286" width="4" height="0" fill="none" />
          {/* deck cargo */}
          {[[SHIP_BAY_X, 422, G300], [SHIP_BAY_X, 374, G200]].map(function (s, i) { return <rect key={'b' + i} x={s[0]} y={s[1]} width={C_W} height={C_H} fill={s[2]} stroke={INK} strokeWidth="2" />; })}
          {deckBays.map(function (bx, i) {
            return tiers.map(function (ty, j) {
              const fill = (i + j) % 3 === 0 ? G300 : (i + j) % 3 === 1 ? G200 : G400;
              return <rect key={bx + '-' + ty} x={bx} y={ty} width={C_W} height={C_H} fill={fill} stroke={INK} strokeWidth="2" />;
            });
          })}
          <rect x="1830" y="326" width={C_W} height={C_H} fill={BLUE} stroke={INK} strokeWidth="2" />
          <text x="1650" y="522" fill="#fff" fontFamily="Figtree" fontWeight="500" fontSize="24" letterSpacing="0.5">Südvik Polar</text>
          <text x="2010" y="522" fill="#fff" fillOpacity="0.7" fontFamily="IBM Plex Mono" fontSize="12">CPH</text>
        </motion.g>

        {/* Our container */}
        <motion.g style={{ x: cx, y: cy }}>
          <rect x="0" y="0" width={C_W} height={C_H} fill={BLUE} stroke={INK} strokeWidth="2.5" />
          {[12, 24, 36, 48, 60, 72, 84, 96].map(function (lx) {
            return <line key={lx} x1={lx} y1="3" x2={lx} y2={C_H - 3} stroke={INK} strokeOpacity="0.18" strokeWidth="2" />;
          })}
          <rect x="6" y="14" width="66" height="22" fill={BLUE} />
          <text x="10" y="31" fill={INK} fontFamily="Figtree" fontWeight="500" fontSize="16" letterSpacing="-0.3">Südvik</text>
          <text x="74" y="14" fill={INK} fontFamily="IBM Plex Mono" fontSize="7">SDVU 482911</text>
          <motion.g style={{ opacity: doorOpacity }}>
            <rect x={C_W - 16} y="0" width="16" height={C_H} fill={BLUE600} stroke={INK} strokeWidth="2.5" />
            <line x1={C_W - 8} y1="5" x2={C_W - 8} y2={C_H - 5} stroke={INK} strokeWidth="2" />
          </motion.g>
        </motion.g>

        <rect x="-600" y="428" width="1900" height="2" fill={INK} opacity="0.5" />
      </g>
    </svg>
  );
}
